import React from 'react';
import { CONTAMINATION_FACTOR_MAX, CONTAMINATION_FACTOR_MIN, buildArrayData } from '../../components/bearing-search/api/api';
import { BearingFatigueLifePayload, TimkenLifeAdjustment, TimkenLubricantType, TimkenOption, TimkenUnit } from '../../components/bearing-search/types/types';
import {
	convertNumberInput,
	isNonZero,
	isValidRange,
	convertTemperature,
	convertLoad,
	checkTempValidation
} from '../utils/validations';
import { handleActionError } from '../utils/engineering-tools';

type FatigueLifeFormData = Omit<BearingFatigueLifePayload, 'partNumber' | 'bearingType' | 'bearingSubType' | 'cupPartNumber' | 'lowloadfactor' | 'isDBRetrieval' | 'isReportRequired'>;

export type FatigueLifeState = {
	option: TimkenOption;
	unit: TimkenUnit;
	lifeAdjustmentFactor: TimkenLifeAdjustment;
	rotationalSpeed?: number;
	radialLoad?: number;
	axialLoad?: number;
	operatingTemp?: number;
	greaseOrOil: TimkenLubricantType;
	lubricantName: string;
	contaminationFactor?: number;
	lubricantSUS100?: number;
	lubricantSUS210?: number;
	lubricantCST100?: number;
	lubricantCST40?: number;
	lubeIndex: number;
	isAdjustmentRequired: boolean;
	isValidated: boolean;
	isLoadValid?: boolean;
	requestData?: FatigueLifeFormData;
	isTempValid?: boolean;
};

export const initialFatigueState: FatigueLifeState = {
	unit: 'M',
	option: 'Timken',
	greaseOrOil: '1',
	lubricantName: '',
	lifeAdjustmentFactor: '6',
	isAdjustmentRequired: true,
	contaminationFactor: 0.1,
	lubeIndex: 5,
	isValidated: false,
	operatingTemp: 20,
	isTempValid: true
};

export type FatigueLifeAction =
	| { type: 'change_unit'; payload: TimkenUnit }
	| { type: 'change_option'; payload: TimkenOption }
	| { type: 'change_life_adjustment'; payload: TimkenLifeAdjustment }
	| { type: 'change_radial_load'; payload: unknown }
	| { type: 'change_axial_load'; payload: unknown }
	| { type: 'change_lub_type'; payload: TimkenLubricantType }
	| { type: 'change_lub_name'; payload: string }
	| { type: 'change_lub_index'; payload: number }
	| { type: 'change_number_field'; payload: [keyof FatigueLifeState, unknown] }
	| { type: 'change_validation_flag'; payload: boolean }
	| { type: 'change_operating_temp'; payload: [string, number, TimkenUnit | unknown]}
	| { type: 'update_request_data' }
	| { type: 'reset'; payload?: FatigueLifeState };

const isLifeAdjustmentsValid = ( state: Pick<FatigueLifeState, 'unit' | 'operatingTemp' | 'lubricantCST40' | 'lubricantCST100' | 'lubricantSUS100' | 'lubricantSUS210'> ): boolean => {
	const {
		unit,
		operatingTemp,
		lubricantCST40,
		lubricantCST100,
		lubricantSUS100,
		lubricantSUS210,
	} = state;

	return operatingTemp === undefined ||
		( unit === 'M' && ( ! isNonZero( lubricantCST100 ) || ! isNonZero( lubricantCST40 ) ) ) ||
		( unit === 'I' && ( ! isNonZero( lubricantSUS100 ) || ! isNonZero( lubricantSUS210 ) ) );
};

const getFatigueLifeFormData = ( state: FatigueLifeState ): FatigueLifeFormData | undefined => {
	const {
		option,
		unit: units,
		isAdjustmentRequired,
		rotationalSpeed,
		axialLoad,
		radialLoad,
		operatingTemp,
		greaseOrOil,
		lubricantName,
		lubeIndex,
		lubricantCST40,
		lubricantCST100,
		lubricantSUS100,
		lubricantSUS210,
		lifeAdjustmentFactor,
		contaminationFactor,
		isTempValid
	} = state;

	// Return undefined if form is not valid
	if (
		! isNonZero( rotationalSpeed ) ||
		( ! isNonZero( axialLoad ) && ! isNonZero( radialLoad ) ) ||
		( isAdjustmentRequired && isLifeAdjustmentsValid( { unit: units, operatingTemp, lubricantCST40, lubricantCST100, lubricantSUS100, lubricantSUS210 } ) ) ||
		( option === 'ISO' && ! isValidRange( contaminationFactor, CONTAMINATION_FACTOR_MIN, CONTAMINATION_FACTOR_MAX ) ) ||
		! isTempValid
	) {
		return undefined;
	}

	// Required fields
	const payload: FatigueLifeFormData = { option, units, rotationalSpeed: buildArrayData( rotationalSpeed ) };

	// Radial / axial load
	payload.axialLoad = isNonZero( axialLoad ) ? buildArrayData( axialLoad ) : [ 0 ];
	payload.radialLoad = isNonZero( radialLoad ) ? buildArrayData( radialLoad ) : [ 0 ];

	if ( option === 'Timken' ) {
		// Life adjustment factor
		payload.lifeAdjustmentFactor = Number( lifeAdjustmentFactor );
	} else {
		// Contamination factor
		payload.contaminationFactor = contaminationFactor;
	}

	// Operating temperature
	payload.operatingTemp = [ 0 ];
	// Lubricant type
	payload.greaseOrOil = '0';
	// Lubricant name
	payload.lubricantName = null;

	if ( ! isAdjustmentRequired ) {
		return payload;
	}

	payload.operatingTemp = buildArrayData( operatingTemp );
	// Lubricant type
	payload.greaseOrOil = greaseOrOil;
	// Lubricant name
	payload.lubricantName = lubricantName;
	// Lubricant index
	payload.lubeIndex = lubeIndex;

	if ( units === 'M' ) {
		// Metric viscosity
		payload.lubricantCST40 = lubricantCST40;
		payload.lubricantCST100 = lubricantCST100;
	} else {
		// Imperial viscosity
		payload.lubricantSUS100 = lubricantSUS100;
		payload.lubricantSUS210 = lubricantSUS210;
	}

	return payload;
};

const fatigueLifeReducer: React.Reducer<
	FatigueLifeState,
	FatigueLifeAction
> = ( state, action ) => {
	switch ( action.type ) {
		case 'change_unit': {
			let operatingTemp = convertTemperature(action.payload, state.operatingTemp);

			return {
				...state,
				isTempValid: checkTempValidation(action.payload, operatingTemp),
				operatingTemp: operatingTemp,
				radialLoad: convertLoad(state.unit, convertNumberInput(state.radialLoad) ),
				axialLoad: convertLoad(state.unit, convertNumberInput(state.axialLoad) ),
				unit: action.payload,
			};
		}
		case 'change_option': {
			return {
				...state,
				option: action.payload,
				isAdjustmentRequired: action.payload === 'ISO' || state.lifeAdjustmentFactor === '6',
			};
		}
		case 'change_life_adjustment': {
			const isAdjustmentRequired = action.payload === '6' || state.option === 'ISO';

			return {
				...state,
				lifeAdjustmentFactor: action.payload,
				isAdjustmentRequired,
			};
		}
		case 'change_radial_load': {
			const value = action.payload;
			return {
				...state,
				radialLoad: convertNumberInput( value ),
				isLoadValid: isNonZero( value ) || isNonZero( state.axialLoad ),
			};
		}
		case 'change_axial_load': {
			const value = action.payload;
			return {
				...state,
				axialLoad: convertNumberInput( value ),
				isLoadValid: isNonZero( value ) || isNonZero( state.radialLoad ),
			};
		}
		case 'change_lub_type': {
			return {
				...state,
				greaseOrOil: action.payload,
			};
		}
		case 'change_lub_name': {
			return {
				...state,
				lubricantName: action.payload,
			};
		}
		case 'change_number_field': {
			const [ key, value ] = action.payload;

			return {
				...state,
				[ key ]: convertNumberInput( value ),
			};
		}

		case 'change_operating_temp': {
			const [ key, value, unit ] = action.payload;

			return {
				...state,
				isTempValid: checkTempValidation(unit, value),
				[ key ]: convertNumberInput( value ),
			}
		}

		case 'change_lub_index': {
			return {
				...state,
				lubeIndex: action.payload,
			};
		}
		case 'change_validation_flag': {
			return {
				...state,
				isValidated: action.payload,
			};
		}
		case 'select_unit': {
			let unit = action.payload;
			let operatingTemp = 20;
			if (unit === 'I') {
				operatingTemp = 68;
			}

			return {
				...state,
				operatingTemp: operatingTemp,
				unit: unit
			}
		}
		case 'update_request_data': {
			return {
				...state,
				requestData: getFatigueLifeFormData( state ),
			};
		}
		case 'reset': {
			const newState = action.payload ? action.payload : initialFatigueState;

			return {
				...newState,
			};
		}
	}

	return handleActionError( action );
};

export default fatigueLifeReducer;
